import type { AxiosError } from 'axios';
import { User, UserManager } from 'oidc-client-ts';

import { oidcConfig } from '../config/oidcConfig';

class KCHandler {
  public userManager: UserManager;
  public config?: { set: (_key: string, _value: string) => void } | null;

  public constructor() {
    this.userManager = new UserManager(oidcConfig);

    this.userManager.events.addUserLoaded(user => {
      this.config?.set('accessToken', user.access_token || '');

      document.dispatchEvent(
        new CustomEvent('ecaTokenUpdated', {
          detail: { token: user.access_token },
        })
      );
    });
  }

  public addConfig(
    config: { set: (_key: string, _value: string) => void } | null
  ): void {
    this.config = config;
  }

  public signInCallback(): Promise<User | undefined> {
    return this.userManager.signinCallback();
  }

  public signInSilentCallback(): Promise<void> {
    return this.userManager.signinSilentCallback();
  }

  public async getToken(): Promise<string | undefined> {
    const user = await this.getUser();

    return user?.access_token;
  }

  public async getIdentityToken(): Promise<string | undefined> {
    const user = await this.getUser();

    return user?.id_token;
  }

  public async login(): Promise<void> {
    await this.userManager.signinRedirect();
  }

  public async signUp(): Promise<void> {
    await this.userManager.signinRedirect({
      extraQueryParams: { screen_hint: 'signup' },
    });
  }

  public async handleError(error: AxiosError): Promise<void> {
    if (error.response?.data && error.response?.status === 403) {
      console.warn('Error: ', error.response?.data);
    } else if (
      error.response?.status === 401 ||
      error.response?.status === 403
    ) {
      await this.login();
    }
  }

  public async logout(): Promise<void> {
    await this.userManager.signoutRedirect();
  }

  public async setUserWithToken(
    accessToken: string,
    expiresIn: number,
    idToken?: string,
    refreshToken?: string
  ): Promise<void> {
    const now = Math.floor(Date.now() / 1000);
    const user = new User({
      access_token: accessToken,
      refresh_token: refreshToken,
      id_token: idToken,
      token_type: 'Bearer',
      expires_at: now + expiresIn,
      profile: {
        sub: '',
        iss: oidcConfig.authority,
        aud: oidcConfig.client_id,
        exp: now + expiresIn,
        iat: now,
      },
    });

    await this.userManager.storeUser(user);
  }

  private async getUser(): Promise<User | null> {
    const user = await this.userManager.getUser();

    if (!user) {
      return null;
    }

    const now = Math.floor(Date.now() / 1000);

    if (user?.expires_at && user.expires_at < now) {
      try {
        const refreshedUser = await this.userManager.signinSilent();

        return refreshedUser;
      } catch (error) {
        console.error('Failed to refresh token:', error);
        await this.login();
      }
    }

    return user;
  }
}

export const kcGateway: KCHandler = new KCHandler();
